import React, { useState, useEffect } from "react";
import { NavLink, Navigate, Routes, Route, Link, useNavigate } from "react-router-dom";
import {
  LayoutDashboard, Wallet, TrendingUp, CreditCard, ListOrdered, Heart,
  ArrowLeftRight, Users, Send, UserPlus, Shield, User, Bot, Bell, LogOut,
  Building2, QrCode, BarChart3, Code, Package, UsersRound, Landmark, FileText,
  Menu, X, Globe, ShoppingBag, GraduationCap, Sparkles
} from "lucide-react";
import { useStore } from "../store";
import api from "../api";
import "./dashboard.css";

import {
  OverviewPage, WalletsPage, StakingPage, CardPage, TransactionsPage,
  CharityPage, ExchangePage, P2PPage, SwiftPage, ReferralPage, SecurityPage,
  ProfilePage, MarketplacePage, AcademyPage,
} from "./user";
import {
  BizOverviewPage, AcquiringPage, SwiftBizPage, AnalyticsPage, ApiPage,
  CatalogPage, TeamPage, WithdrawPage, DocsPage,
} from "./business";
import {
  MerchantOverviewPage, ZeroLegalPage, PayrollPage, AcquiringPage2, SupplyChainPage,
} from "./merchant2";
import AdminAiPendingPage from "./AdminAiPending";
import { CorporateBankingPage } from "./CorporateBanking";
import {
  AdminOverviewPage, AdminUsersPage, AdminKycPage, AdminTransactionsPage,
  AdminOrdersPage, AdminProductsPage, AdminLeaderboardPage, AdminInvestorLeadsPage,} from "./admin";
import AdminUnlock from "./AdminUnlock";
import TelegramGate from "./TelegramGate";
import TurboAiSupport from "./TurboAiSupport";
import { useT } from "../site/i18n";

// STEP 23 — Locked pre-launch features (Staking frozen, Academy & Charity pre-launch).
// Clicking these tabs is a no-op; a 🔒 badge is rendered next to the label.
const LOCKED_ROUTES = new Set(["staking", "academy", "charity"]);
const STAKING_SYSTEM_FROZEN = true;  // global system state — enforces UI freeze

// Renders a locked/frozen placeholder for pre-launch routes.
function FrozenFeaturePage({ title, reason }) {
  return (
    <div data-testid="frozen-feature-page"
      style={{ display: "flex", flexDirection: "column", alignItems: "center",
               justifyContent: "center", minHeight: "60vh", padding: 40 }}>
      <div style={{ fontSize: 64, marginBottom: 12 }}>🔒</div>
      <h1 data-testid="frozen-feature-title"
        style={{ fontSize: 28, color: "#FFD700", margin: "0 0 12px", textAlign: "center" }}>
        {title}
      </h1>
      <p style={{ fontSize: 14, color: "#8B94B8", maxWidth: 480, textAlign: "center", lineHeight: 1.6 }}>
        {reason}
      </p>
      <a href="/dashboard" data-testid="frozen-feature-back"
        style={{ marginTop: 24, padding: "10px 22px", borderRadius: 10,
                 background: "rgba(0,242,255,0.10)",
                 border: "1px solid rgba(0,242,255,0.40)",
                 color: "#00F2FF", textDecoration: "none", fontWeight: 700 }}>
        ← Вернуться на Overview
      </a>
    </div>
  );
}

const buildUserNav = (t) => [
  { group: t("side.group.overview"), items: [["", t("nav.overview"), LayoutDashboard]] },
  { group: t("side.group.finance"),  items: [
    ["wallets",      t("nav.wallets"),      Wallet],
    ["staking",      t("nav.staking"),      TrendingUp,     { locked: true, reason: "Заморожено" }],
    ["card",         t("nav.visa"),         CreditCard],
    ["transactions", t("nav.transactions"), ListOrdered],
    ["charity",      t("nav.charity"),      Heart,          { locked: true, reason: "Скоро" }],
  ]},
  { group: t("side.group.trade"), items: [
    ["exchange", t("nav.exchange"), ArrowLeftRight],
    ["p2p",      t("nav.p2p"),      Users],
    ["swift",    t("nav.swift"),    Send],
  ]},
  { group: t("side.group.network"), items: [
    ["referrals",   t("nav.referrals"),   UserPlus],
    ["marketplace", t("nav.marketplace"), ShoppingBag],
    ["academy",     t("nav.academy"),     GraduationCap,   { locked: true, reason: "Скоро" }],
  ]},
  { group: t("side.group.account"), items: [
    ["security", t("nav.security"), Shield],
    ["profile",  t("nav.profile"),  User],
  ]},
];

const buildBizNav = (t) => [
  { group: "Merchant 2.0", items: [["merchant", "Overview", LayoutDashboard]] },
  { group: "Hybrid Banking", items: [
    ["merchant/banking", "Corporate Account", Landmark],
  ]},
  { group: "AI Compliance", items: [
    ["merchant/legal", "Zero-Legal AI", Shield],
  ]},
  { group: "Payroll & HR", items: [
    ["merchant/payroll", "Turbo-Zarplata", UsersRound],
  ]},
  { group: "Acquiring", items: [
    ["merchant/acquiring", "Terminals + Smart Tax", QrCode],
  ]},
  { group: "Supply Chain", items: [
    ["merchant/supply", "AI Co-Pilot + B2B POs", Package],
  ]},
  { group: "Legacy (v1)", items: [
    ["biz",          "Legacy Business",    Building2],
    ["biz/swift",    t("nav.biz_swift"),    Send],
    ["biz/withdraw", t("nav.biz_withdraw"), Landmark],
    ["biz/api",      t("nav.biz_api"),      Code],
  ]},
];

const buildAdminNav = (t) => [
  { group: t("side.group.admin"), items: [["admin", t("nav.admin_overview"), LayoutDashboard]] },
  { group: t("side.group.management"), items: [
    ["admin/users",       t("nav.admin_users"),    UsersRound],
    ["admin/kyc",         t("nav.admin_kyc"),      Shield],
    ["admin/products",    t("nav.admin_products"), Package],
    ["admin/leaderboard", "🏆 Лидерборд",          BarChart3],
    ["admin/investor-leads", "💼 Заявки инвесторов", Shield],
    ["admin/ai-pending", "🤖 AI Pending Review", Sparkles],
  ]},
  { group: t("side.group.records"), items: [
    ["admin/transactions", t("nav.admin_transactions"), ListOrdered],
    ["admin/orders",       t("nav.admin_orders"),       ShoppingBag],
  ]},
];

export default function Dashboard() {
  const { token, user, wallets: storeWallets, fetchAll, logout } = useStore();
  const navigate = useNavigate();
  const { t } = useT();
  const USER_NAV = buildUserNav(t);
  const BIZ_NAV = buildBizNav(t);
  const ADMIN_NAV = buildAdminNav(t);
  const [role, setRole] = useState(() => localStorage.getItem("dash_role") || "user");
  const [bellOpen, setBellOpen] = useState(false);
  const [aiOpen, setAiOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [notifs, setNotifs] = useState([]);
  const [unread, setUnread] = useState(0);
  const [regStatus, setRegStatus] = useState(null);   // STEP 15 — payment gate
  const [bizViewMode, setBizViewMode] = useState(() => {   // STEP 16 — dual-dashboard toggle
    try { return localStorage.getItem("tgc_biz_view_mode") || "BUSINESS_MODE"; }
    catch (e) { return "BUSINESS_MODE"; }
  });
  const isMerchantTier = (regStatus?.account_type === "MERCHANT_RETAIL" ||
                          regStatus?.account_type === "MERCHANT_ENTERPRISE" ||
                          user?.role === "admin");   // admins can always toggle
  const flipBizMode = () => {
    const next = bizViewMode === "BUSINESS_MODE" ? "PROVIDER_MODE" : "BUSINESS_MODE";
    setBizViewMode(next);
    try { localStorage.setItem("tgc_biz_view_mode", next); } catch (e) { /* noop */ }
    // Navigate to the mode's landing page so URL doesn't get stuck on a page
    // that doesn't exist in the other nav.
    if (next === "PROVIDER_MODE") navigate("/dashboard");
    else                          navigate("/dashboard/merchant");
  };

  useEffect(() => { if (token) fetchAll().catch(() => {}); }, [token, fetchAll]);
  useEffect(() => {
    if (!token) return;
    api.get("/notifications/my").then((r) => { setNotifs(r.data.notifications); setUnread(r.data.unread); }).catch(() => {});
  }, [token]);
  // STEP 15 — poll payment gate status (freezes dashboard until Polygon tx clears)
  useEffect(() => {
    if (!token) return;
    const poll = () => api.get("/registration/status")
      .then((r) => setRegStatus(r.data))
      .catch(() => setRegStatus(null));
    poll();
    const iv = setInterval(poll, 15000);
    return () => clearInterval(iv);
  }, [token]);

  if (!token) return <Navigate to="/login" replace />;
  if (!user) return <div style={{ padding: 40, color: "#fff", background: "#050505", minHeight: "100vh" }}>{t("common.loading")}</div>;

  // STEP 15 — Payment paywall. Admin bypasses. Merchant/Provider must clear the
  // Polygon tx before the dashboard is un-frozen.
  const isAdminBypass = user.role === "admin";
  if (regStatus && regStatus.gate_active && !isAdminBypass) {
    return <RegistrationPaywall status={regStatus} user={user} />;
  }

  const setRolePersist = (r) => {
    setRole(r); localStorage.setItem("dash_role", r); setMobileOpen(false);
    const path = r === "biz" ? "/dashboard/merchant" : r === "admin" ? "/dashboard/admin" : "/dashboard";
    navigate(path);
  };
  const isAdmin = user.role === "admin";
  // Force regular role if non-admin tried to enter admin via localStorage
  const effectiveRole = (role === "admin" && !isAdmin) ? "user" : role;
  // STEP 16 — dual-dashboard toggle: when merchant is in "biz" role + PROVIDER_MODE,
  // render the user-side Network nav (referrals + tree + AI hunter) inside their session.
  const merchantAsProvider = (effectiveRole === "biz" && isMerchantTier && bizViewMode === "PROVIDER_MODE");
  const NAV = merchantAsProvider ? USER_NAV
             : effectiveRole === "biz" ? BIZ_NAV
             : effectiveRole === "admin" ? ADMIN_NAV
             : USER_NAV;
  const wallets = storeWallets || user.wallets || { cashTgc: 0, shoppingTgc: 0, pensionTgc: 0, insuranceTgc: 0 };
  const totalTGC = (wallets.cashTgc || 0) + (wallets.shoppingTgc || 0) + (wallets.pensionTgc || 0) + (wallets.insuranceTgc || 0);
  const totalEUR = totalTGC * 0.10;

  return (
    <div className="dash-root">
      {/* SIDEBAR */}
      <aside className={`dash-sidebar ${mobileOpen ? "mobile-open" : ""}`}>
        <Link to="/" className="dash-sidebar-logo">
          <img src="/tgc-logo.png" alt="TGC" style={{ width: 140, height: 140, objectFit: "contain", filter: "drop-shadow(0 0 16px rgba(255,200,50,0.55))" }} />
          <span style={{ fontSize: 22, fontWeight: 900 }}>TURBO <span style={{ color: "#00F2FF" }}>GLOBAL</span></span>
        </Link>
        <div className="dash-role-switch">
          <button className={`dash-role-btn ${effectiveRole === "user" ? "active" : ""}`} onClick={() => setRolePersist("user")} data-testid="role-user">{t("dash.role.user")}</button>
          <button className={`dash-role-btn ${effectiveRole === "biz" ? "active" : ""}`} onClick={() => setRolePersist("biz")} data-testid="role-biz">{t("dash.role.biz")}</button>
          {isAdmin && (
            <button className={`dash-role-btn ${effectiveRole === "admin" ? "active" : ""}`} onClick={() => setRolePersist("admin")} data-testid="role-admin" style={{ background: effectiveRole === "admin" ? "#FFD700" : undefined, color: effectiveRole === "admin" ? "#000" : undefined, borderColor: effectiveRole === "admin" ? "#FFD700" : undefined }}>{t("dash.role.admin")}</button>
          )}
        </div>
        {/* STEP 16 — Dual-Dashboard Toggle (only for merchant tiers) */}
        {effectiveRole === "biz" && (
          <button
            data-testid="biz-view-mode-toggle"
            onClick={isMerchantTier ? flipBizMode : undefined}
            disabled={!isMerchantTier}
            title={isMerchantTier
              ? "Switch to Provider View / Network Tree"
              : "Pure PROVIDER tier — locked to networking view"}
            style={{
              margin: "10px 12px 4px",
              padding: "10px 12px",
              borderRadius: 10,
              border: `1px solid ${bizViewMode === "PROVIDER_MODE" ? "#00F2FF" : "#FFD700"}`,
              background: !isMerchantTier ? "rgba(255,255,255,0.03)"
                : bizViewMode === "PROVIDER_MODE"
                  ? "linear-gradient(135deg, rgba(0,242,255,0.20), rgba(188,19,254,0.10))"
                  : "linear-gradient(135deg, rgba(255,215,0,0.18), rgba(255,159,67,0.10))",
              color: !isMerchantTier ? "#5F6884"
                : bizViewMode === "PROVIDER_MODE" ? "#00F2FF" : "#FFD700",
              cursor: isMerchantTier ? "pointer" : "not-allowed",
              display: "flex",
              alignItems: "center",
              gap: 10,
              fontSize: 12,
              fontWeight: 800,
              letterSpacing: 0.4,
              textAlign: "left",
              transition: "all 0.18s ease",
              boxShadow: isMerchantTier
                ? `0 6px 20px ${bizViewMode === "PROVIDER_MODE" ? "rgba(0,242,255,0.20)" : "rgba(255,215,0,0.22)"}`
                : "none",
              opacity: isMerchantTier ? 1 : 0.55,
            }}>
            {/* Sliding toggle knob */}
            <span style={{
              position: "relative", width: 32, height: 18, borderRadius: 999,
              background: bizViewMode === "PROVIDER_MODE" ? "rgba(0,242,255,0.20)" : "rgba(255,215,0,0.20)",
              border: `1px solid ${bizViewMode === "PROVIDER_MODE" ? "#00F2FF" : "#FFD700"}`,
              flexShrink: 0, transition: "background 0.18s",
            }}>
              <span style={{
                position: "absolute", top: 1, left: bizViewMode === "PROVIDER_MODE" ? 14 : 1,
                width: 14, height: 14, borderRadius: 999,
                background: bizViewMode === "PROVIDER_MODE" ? "#00F2FF" : "#FFD700",
                transition: "left 0.22s cubic-bezier(0.4, 0, 0.2, 1)",
                boxShadow: "0 2px 6px rgba(0,0,0,0.35)",
              }} />
            </span>
            <div style={{ flex: 1, lineHeight: 1.3 }}>
              <div data-testid="biz-view-mode-label" style={{ fontSize: 11, opacity: 0.85 }}>
                {bizViewMode === "PROVIDER_MODE" ? "Provider View · Network Tree" : "Business Mode · ERP"}
              </div>
              <div style={{ fontSize: 10, color: "#8B94B8", marginTop: 1 }}>
                {!isMerchantTier ? "Locked — Provider tier ($10)" :
                  bizViewMode === "PROVIDER_MODE"
                    ? "→ Switch to Business ERP"
                    : "→ Switch to Provider Network"}
              </div>
            </div>
          </button>
        )}
        <nav className="dash-nav">
          {NAV.map((g, i) => (
            <div key={i} className="dash-nav-group">
              <div className="dash-nav-title">{g.group}</div>
              {g.items.map(([to, label, Icon, meta]) => {
                const locked = meta?.locked;
                if (locked) {
                  return (
                    <div key={to} data-testid={`dash-nav-${to}-locked`}
                      onClick={(e) => e.preventDefault()}
                      title={meta.reason || "Заблокировано"}
                      className="dash-nav-item"
                      style={{
                        cursor: "not-allowed", opacity: 0.42, filter: "blur(0.4px)",
                        pointerEvents: "auto", userSelect: "none",
                        display: "flex", alignItems: "center", gap: 6,
                      }}>
                      <Icon size={16} />
                      <span style={{ flex: 1 }}>{label}</span>
                      <span style={{ fontSize: 10, fontWeight: 800, color: "#FF3D6C",
                                     background: "rgba(255,61,108,0.10)",
                                     border: "1px solid rgba(255,61,108,0.35)",
                                     borderRadius: 999, padding: "1px 7px",
                                     letterSpacing: 0.8 }}>
                        🔒 {meta.reason || "Locked"}
                      </span>
                    </div>
                  );
                }
                return (
                  <NavLink key={to} to={`/dashboard${to ? "/" + to : ""}`} end={!to}
                    className={({ isActive }) => `dash-nav-item ${isActive ? "active" : ""}`}
                    onClick={() => setMobileOpen(false)}
                    data-testid={`dash-nav-${to || "home"}`}>
                    <Icon size={16} /> {label}
                  </NavLink>
                );
              })}
            </div>
          ))}
          <div className="dash-nav-group">
            <button className="dash-nav-item" onClick={() => setAiOpen(true)} data-testid="open-ai-drawer"><Bot size={16} /> {t("dash.ai_assistant")}</button>
            <button className="dash-nav-item" onClick={() => { logout(); window.location.href = "/"; }} data-testid="dash-logout" style={{ color: "#FF3D6C" }}><LogOut size={16} /> {t("dash.logout")}</button>
          </div>
        </nav>
      </aside>

      {/* MAIN */}
      <div className="dash-main">
        <header className="dash-header">
          <button className="dash-burger" onClick={() => setMobileOpen(!mobileOpen)} data-testid="dash-burger">
            {mobileOpen ? <X size={18} /> : <Menu size={18} />}
          </button>
          <h1>{effectiveRole === "admin" ? t("dash.title.admin") :
                merchantAsProvider ? "Provider View · Network Tree" :
                effectiveRole === "biz" ? t("dash.title.biz") :
                t("dash.title.user")}</h1>
          <div className="spacer" />
          <div className="dash-balance-chip" data-testid="total-balance">
            {totalTGC.toFixed(2)} TGC · €{totalEUR.toFixed(2)}
          </div>
          <button className="dash-icon-btn" onClick={() => setBellOpen(true)} data-testid="bell-btn">
            <Bell size={18} />
            {unread > 0 && <span className="dash-bell-dot" />}
          </button>
          <div style={{ width: 38, height: 38, borderRadius: 19, background: "linear-gradient(135deg,#00F2FF,#7C3AED)", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 800, color: "#000" }}>
            {user.firstName?.[0] || "U"}{user.lastName?.[0] || ""}
          </div>
        </header>

        <main className="dash-content">
          <TelegramGate userRole={effectiveRole}>
          <Routes>
            {/* User routes */}
            <Route index element={<OverviewPage />} />
            <Route path="wallets"      element={<WalletsPage />} />
            <Route path="staking"      element={<FrozenFeaturePage title="Стейкинг · заморожен" reason="Стейкинг-модуль (5 пакетов 8-20 %) заморожен до старта продакшн-цепочки." />} />
            <Route path="card"         element={<CardPage />} />
            <Route path="transactions" element={<TransactionsPage />} />
            <Route path="charity"      element={<FrozenFeaturePage title="Bear Charity · скоро" reason="Модуль благотворительности будет запущен после публичного релиза TGC-Bank." />} />
            <Route path="exchange"     element={<ExchangePage />} />
            <Route path="p2p"          element={<P2PPage />} />
            <Route path="swift"        element={<SwiftPage />} />
            <Route path="referrals"    element={<ReferralPage />} />
            <Route path="marketplace"  element={<MarketplacePage />} />
            <Route path="academy"      element={<FrozenFeaturePage title="Turbo Academy · скоро" reason="Академия готовится к публичному запуску вместе с сертификатами блокчейна." />} />
            <Route path="security"     element={<SecurityPage />} />
            <Route path="profile"      element={<ProfilePage />} />
            {/* Business routes */}
            <Route path="biz"            element={<BizOverviewPage />} />
            <Route path="biz/acquiring"  element={<AcquiringPage />} />
            <Route path="biz/swift"      element={<SwiftBizPage />} />
            <Route path="biz/analytics"  element={<AnalyticsPage />} />
            <Route path="biz/api"        element={<ApiPage />} />
            <Route path="biz/catalog"    element={<CatalogPage />} />
            <Route path="biz/team"       element={<TeamPage />} />
            <Route path="biz/withdraw"   element={<WithdrawPage />} />
            <Route path="biz/docs"       element={<DocsPage />} />
            {/* Merchant 2.0 — STEP 12 */}
            <Route path="merchant"            element={<MerchantOverviewPage />} />
            <Route path="merchant/banking"    element={<CorporateBankingPage />} />
            <Route path="merchant/legal"      element={<ZeroLegalPage />} />
            <Route path="merchant/payroll"    element={<PayrollPage />} />
            <Route path="merchant/acquiring"  element={<AcquiringPage2 />} />
            <Route path="merchant/supply"     element={<SupplyChainPage />} />
            {/* Admin routes — protected: only role=="admin" AND ADMIN_ACCESS_TOKEN unlock */}
            <Route path="admin"             element={isAdmin ? <AdminUnlock><AdminOverviewPage /></AdminUnlock>     : <Navigate to="/dashboard" replace />} />
            <Route path="admin/users"       element={isAdmin ? <AdminUnlock><AdminUsersPage /></AdminUnlock>        : <Navigate to="/dashboard" replace />} />
            <Route path="admin/kyc"         element={isAdmin ? <AdminUnlock><AdminKycPage /></AdminUnlock>          : <Navigate to="/dashboard" replace />} />
            <Route path="admin/transactions"element={isAdmin ? <AdminUnlock><AdminTransactionsPage /></AdminUnlock> : <Navigate to="/dashboard" replace />} />
            <Route path="admin/orders"      element={isAdmin ? <AdminUnlock><AdminOrdersPage /></AdminUnlock>       : <Navigate to="/dashboard" replace />} />
            <Route path="admin/products"    element={isAdmin ? <AdminUnlock><AdminProductsPage /></AdminUnlock>     : <Navigate to="/dashboard" replace />} />
            <Route path="admin/leaderboard" element={isAdmin ? <AdminUnlock><AdminLeaderboardPage /></AdminUnlock> : <Navigate to="/dashboard" replace />} />
            <Route path="admin/investor-leads" element={isAdmin ? <AdminUnlock><AdminInvestorLeadsPage /></AdminUnlock> : <Navigate to="/dashboard" replace />} />
            <Route path="admin/ai-pending"    element={isAdmin ? <AdminUnlock><AdminAiPendingPage /></AdminUnlock>    : <Navigate to="/dashboard" replace />} />
          </Routes>
          </TelegramGate>
          {/* STEP 19 — Turbo AI Support docked on BOTH User and Merchant dashboards */}
          <TurboAiSupport />
        </main>
      </div>

      {/* BELL DRAWER */}
      {bellOpen && (
        <div className="dash-drawer" data-testid="bell-drawer">
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
            <h3 style={{ margin: 0 }}>🔔 {t("dash.notifications")}</h3>
            <button onClick={() => setBellOpen(false)} className="dash-icon-btn"><X size={18} /></button>
          </div>
          {notifs.map((n) => (
            <div key={n.id} style={{ padding: 14, borderRadius: 12, border: "1px solid var(--tg-border)", marginBottom: 10, background: n.read ? "transparent" : "rgba(0,242,255,0.04)" }}>
              <div style={{ fontWeight: 700, fontSize: 14 }}>{n.title}</div>
              <div style={{ fontSize: 13, color: "var(--tg-text-dim)", marginTop: 4, lineHeight: 1.5 }}>{n.body}</div>
            </div>
          ))}
        </div>
      )}

      {/* AI DRAWER */}
      {aiOpen && (
        <div className="dash-drawer" data-testid="ai-drawer">
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
            <h3 style={{ margin: 0 }}><Bot size={18} style={{ verticalAlign: "middle" }} /> Turbo AI Assistant</h3>
            <button onClick={() => setAiOpen(false)} className="dash-icon-btn"><X size={18} /></button>
          </div>
          <AIChat />
        </div>
      )}
    </div>
  );
}

/* Reusable AI chat inside drawer */
function AIChat() {
  const { t } = useT();
  const [msgs, setMsgs] = useState([{ role: "ai", text: t("ai.greeting") }]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const send = async () => {
    if (!input.trim()) return;
    const userMsg = input;
    setMsgs((m) => [...m, { role: "user", text: userMsg }]);
    setInput(""); setLoading(true);
    try {
      const r = await api.post("/ai/chat", { message: userMsg });
      setMsgs((m) => [...m, { role: "ai", text: r.data.reply || r.data.response || "..." }]);
    } catch (e) {
      setMsgs((m) => [...m, { role: "ai", text: t("ai.error") }]);
    } finally { setLoading(false); }
  };
  return (
    <div style={{ display: "flex", flexDirection: "column", height: "calc(100vh - 100px)" }}>
      <div style={{ flex: 1, overflowY: "auto", paddingRight: 8 }}>
        {msgs.map((m, i) => (
          <div key={i} style={{ marginBottom: 12, padding: "10px 14px", borderRadius: 14,
            background: m.role === "user" ? "rgba(0,242,255,0.1)" : "rgba(255,255,255,0.03)",
            alignSelf: m.role === "user" ? "flex-end" : "flex-start",
            maxWidth: "85%", fontSize: 13, lineHeight: 1.5, marginLeft: m.role === "user" ? "auto" : 0 }}>
            {m.text}
          </div>
        ))}
        {loading && <div style={{ color: "var(--tg-text-hint)", fontSize: 12 }}>{t("ai.typing")}</div>}
      </div>
      <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
        <input className="auth-input" placeholder={t("ai.placeholder")} value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && send()}
          style={{ padding: "10px 12px" }} />
        <button className="tg-btn primary" onClick={send} disabled={loading} style={{ padding: "8px 14px" }}>→</button>
      </div>
    </div>
  );
}


/* ══════════════════ STEP 15 · Registration Paywall ══════════════════ */
function RegistrationPaywall({ status, user }) {
  const TIER_META = {
    PROVIDER:            { label: "Network Provider",       color: "#00F2FF", icon: "🛰️" },
    MERCHANT_RETAIL:     { label: "Retail Outlet & Services", color: "#FFD700", icon: "🏪" },
    MERCHANT_ENTERPRISE: { label: "Enterprise & Factory",   color: "#BC13FE", icon: "🏭" },
  };
  const meta = TIER_META[status.account_type] || TIER_META.PROVIDER;
  const onboarding = `/onboarding?tier=${encodeURIComponent(status.account_type)}`;
  const isMerchantTier = status.account_type === "MERCHANT_RETAIL" || status.account_type === "MERCHANT_ENTERPRISE";
  // STEP 21 — surface Merchant Quick-Reply chips right inside the paywall so
  // shop owners / factory directors can verify ERP + acquiring behaviour BEFORE
  // committing 50/250 USDT on-chain.
  const [chipMsg, setChipMsg] = useState(null);   // { question, answer, confidence, topic, loading }
  const askChip = async (question) => {
    setChipMsg({ question, loading: true });
    try {
      const r = await api.post("/support/ask", { query: question, lang: "ru" });
      setChipMsg({ question, ...r.data, loading: false });
    } catch (e) {
      setChipMsg({ question, error: e?.response?.data?.detail || "Support engine unreachable", loading: false });
    }
  };
  const CHIPS = [
    "Как настроить Turbo-Zarplata?",
    "Как включить эквайринг 0.4%?",
  ];
  return (
    <div data-testid="registration-paywall"
      style={{
        minHeight: "100vh", background: "linear-gradient(135deg, #050914 0%, #0B1220 100%)",
        display: "flex", alignItems: "center", justifyContent: "center", padding: 24,
      }}>
      <div style={{
        maxWidth: 560, width: "100%",
        background: "linear-gradient(180deg, #0B1220 0%, #050914 100%)",
        border: `1px solid ${meta.color}`, borderRadius: 18, padding: 32,
        boxShadow: `0 30px 80px ${meta.color}20`,
      }}>
        <div style={{ textAlign: "center", marginBottom: 20 }}>
          <div style={{ fontSize: 56 }}>{meta.icon}</div>
          <div style={{ fontSize: 11, color: meta.color, letterSpacing: 2, textTransform: "uppercase", marginTop: 6, fontWeight: 800 }}>
            Регистрация · Оплата ожидания
          </div>
          <h2 style={{ margin: "8px 0 4px", fontSize: 22, color: "#E7ECFF" }}>{meta.label}</h2>
          <div style={{ fontSize: 13, color: "#8B94B8" }}>Ваш кабинет заморожен до подтверждения оплаты в Polygon.</div>
        </div>
        <div style={{ padding: 16, borderRadius: 12,
                      background: `linear-gradient(135deg, ${meta.color}12, rgba(255,255,255,0.02))`,
                      border: `1px solid ${meta.color}45`, marginBottom: 18 }}>
          <div style={{ fontSize: 11, color: "#8B94B8", letterSpacing: 1, textTransform: "uppercase" }}>
            Требуется активация
          </div>
          <div style={{ fontSize: 36, fontWeight: 900, color: meta.color, marginTop: 4, fontFeatureSettings: '"tnum"' }}>
            ${Number(status.required_fee_usdt || 0).toFixed(0)} <span style={{ fontSize: 14, opacity: 0.75 }}>USDT · Polygon</span>
          </div>
          <div style={{ fontSize: 12, color: "#8B94B8", marginTop: 6, lineHeight: 1.5 }}>
            100 % капитала конвертируется в locked TGC токены под защитой Fiat Stability Lock.
            До подтверждения транзакции доступ к дашборду и AI-модулям заблокирован.
          </div>
        </div>
        <a href={onboarding} data-testid="paywall-cta-onboarding"
          style={{ display: "block", padding: "14px 20px", borderRadius: 12, textDecoration: "none",
                   textAlign: "center", fontWeight: 800, fontSize: 15,
                   background: `linear-gradient(135deg, ${meta.color}, #FFD700)`,
                   color: "#050914", marginBottom: 10 }}>
          Продолжить в Web3 Onboarding →
        </a>

        {/* STEP 21 — Pre-payment Merchant Quick-Reply Chips */}
        {isMerchantTier && (
          <div data-testid="paywall-merchant-chips-block"
            style={{ marginTop: 6, marginBottom: 10, padding: 12, borderRadius: 12,
                     background: "rgba(255,215,0,0.05)",
                     border: "1px solid rgba(255,215,0,0.30)" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 8 }}>
              <div style={{ width: 6, height: 6, borderRadius: "50%", background: "#FFD700",
                            boxShadow: "0 0 8px #FFD700" }} />
              <div style={{ fontSize: 10, fontWeight: 800, color: "#FFD700",
                            letterSpacing: 1.4, textTransform: "uppercase" }}>
                Проверить перед оплатой
              </div>
            </div>
            <div style={{ fontSize: 11, color: "#8B94B8", lineHeight: 1.4, marginBottom: 8 }}>
              Убедитесь, как работает ERP и эквайринг 0.4 % ДО перевода {Number(status.required_fee_usdt || 0).toFixed(0)} USDT.
            </div>
            <div data-testid="paywall-merchant-chips"
              style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: chipMsg ? 10 : 0 }}>
              {CHIPS.map((label, i) => (
                <button key={i} onClick={() => askChip(label)}
                  disabled={chipMsg?.loading}
                  data-testid={`paywall-merchant-chip-${i}`}
                  style={{
                    padding: "6px 10px", borderRadius: 999, fontSize: 11, fontWeight: 600,
                    color: chipMsg?.loading ? "#5F6884" : "#FFD700",
                    background: chipMsg?.loading ? "rgba(255,255,255,0.03)" : "rgba(255,215,0,0.10)",
                    border: `1px solid ${chipMsg?.loading ? "rgba(255,255,255,0.10)" : "rgba(255,215,0,0.40)"}`,
                    cursor: chipMsg?.loading ? "wait" : "pointer",
                    transition: "all 0.18s ease", whiteSpace: "nowrap",
                  }}>
                  {label}
                </button>
              ))}
            </div>
            {chipMsg && (
              <div data-testid="paywall-merchant-chip-answer"
                style={{ padding: 10, borderRadius: 8,
                         background: "rgba(0,0,0,0.30)",
                         border: "1px solid rgba(255,215,0,0.20)",
                         maxHeight: 220, overflowY: "auto" }}>
                <div style={{ fontSize: 10, color: "#8B94B8", marginBottom: 4 }}>
                  {chipMsg.question}
                </div>
                {chipMsg.loading ? (
                  <div style={{ fontSize: 12, color: "#8B94B8" }}>Turbo AI отвечает…</div>
                ) : chipMsg.error ? (
                  <div style={{ fontSize: 12, color: "#FF3366" }}>{chipMsg.error}</div>
                ) : (
                  <>
                    <div style={{ fontSize: 12, color: "#E7ECFF", whiteSpace: "pre-wrap", lineHeight: 1.5 }}>
                      {chipMsg.answer}
                    </div>
                    {chipMsg.confidence != null && (
                      <div style={{ fontSize: 10, color: "#10F4A7", marginTop: 6 }}>
                        Resolved · {Math.round((chipMsg.confidence || 0) * 100)}% confidence · {chipMsg.matched_topic || chipMsg.topic}
                      </div>
                    )}
                  </>
                )}
              </div>
            )}
          </div>
        )}

        <button onClick={() => window.location.reload()} data-testid="paywall-refresh"
          style={{ width: "100%", padding: "10px 12px", borderRadius: 10, border: "1px solid rgba(255,255,255,0.10)",
                   background: "transparent", color: "#8B94B8", cursor: "pointer", fontSize: 12 }}>
          Обновить статус (авто-проверка каждые 15 сек)
        </button>
        <div style={{ marginTop: 18, fontSize: 10, color: "#5F6884", textAlign: "center" }}>
          user: <code>{user?.email}</code> · tier: <code>{status.account_type}</code>
        </div>
      </div>
    </div>
  );
}
